import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Page, PageErrorState, Typography } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { routes } from '~/constants';
import { useIntegrationData } from '~/hooks/reactQuery/derived/useIntegrationData';
import { useHistoryLength } from '~/hooks/useNavigationBlocker';
import { useLocaleKeys } from '~/i18n';
import { Intent } from '~/infra/access-control';
import { isLinked } from '~/models/integration';
import { Tab } from '~/pages/NotificationSettings/NotificationSettings.utils';
import { selectors } from '~/store/state-selectors';
import { useStore } from '~/store/store-context';
import { getIntegrationDisplayName, getManageIntegrationRoute } from './ConnectedApps.utils';
import styles from './ConnectedAppsPage.module.scss';
export const GetConnectedAppsPageTID = (parentTestId)=>getTestIds({
        parentTestId,
        deprecatedTestIds: {
            ConnectedAppsPageTID: {
                ErrorState: 'integrations-error-state',
                Page: 'connected-apps-page',
                Integration: (source)=>"integration-".concat(source),
                ManageButton: (source)=>"manage-integration-button-".concat(source),
                EmptyState: 'empty-state-container',
                ExploreAppsButton: 'explore-apps-button'
            }
        }
    });
export default function ConnectedAppsPage() {
    const { t: connectedAppsPageTexts } = useLocaleKeys('integrations.connectedApps');
    const { t: stanfordTaxTexts } = useLocaleKeys('integrations.setup.stanfordTax');
    const { ConnectedAppsPageTID } = useMemo(()=>GetConnectedAppsPageTID(), []);
    const isVendor = useStore(selectors.user.isVendor);
    const { data: integrations, error, refresh: refreshIntegrations } = useIntegrationData({
        enabled: isVendor
    });
    const navigate = useNavigate();
    const historyLength = useHistoryLength();
    const onBack = ()=>{
        if (historyLength > 1) {
            navigate(-1);
        } else {
            navigate(routes.availableApps);
        }
    };
    const onExploreApps = ()=>{
        navigate(routes.availableApps);
    };
    const sortedAndFilteredIntegrations = useMemo(()=>integrations ? [
            ...integrations
        ].sort((a, b)=>a.source.localeCompare(b.source)).filter((integration)=>isLinked(integration)) : [], [
        integrations
    ]);
    const onManageIntegration = (integration)=>{
        if (integration.source === 'gmail' || integration.source === 'outlook') {
            navigate(routes.notifications, {
                state: {
                    selectedTab: Tab.Client
                }
            });
            return;
        }
        navigate(getManageIntegrationRoute(integration));
    };
    if (error) {
        return /*#__PURE__*/ _jsx(PageErrorState, {
            testId: ConnectedAppsPageTID.ErrorState(),
            title: 'Error loading integrations',
            onRefresh: ()=>refreshIntegrations({
                    throwOnError: false
                })
        });
    }
    return /*#__PURE__*/ _jsx("div", {
        className: styles.connectedAppsWrapper,
        children: /*#__PURE__*/ _jsx(Page, {
            header: connectedAppsPageTexts.header(),
            subheader: connectedAppsPageTexts.subheader(),
            onClickBack: onBack,
            testId: ConnectedAppsPageTID.Page(),
            children: /*#__PURE__*/ _jsx("div", {
                className: styles.integrationsContainer,
                children: sortedAndFilteredIntegrations.length > 0 ? sortedAndFilteredIntegrations.map((integration)=>/*#__PURE__*/ _jsxs("div", {
                        className: styles.integration,
                        "data-testid": ConnectedAppsPageTID.Integration(integration.source),
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                variant: "h6",
                                className: styles.integrationHeader,
                                testId: ConnectedAppsPageTID.Title(integration.source),
                                children: getIntegrationDisplayName(integration, stanfordTaxTexts.name())
                            }),
                            /*#__PURE__*/ _jsx(Button, {
                                bi: {
                                    name: 'manage_connected_app',
                                    data: {
                                        source: integration.source
                                    }
                                },
                                testId: ConnectedAppsPageTID.ManageButton(integration.source),
                                size: "medium",
                                intent: Intent.ConnectIntegration,
                                onClick: ()=>onManageIntegration(integration),
                                className: styles.manageButton,
                                children: connectedAppsPageTexts.manage()
                            })
                        ]
                    }, integration.source)) : /*#__PURE__*/ _jsxs("div", {
                    className: styles.emptyStateWrapper,
                    "data-testid": ConnectedAppsPageTID.EmptyState(),
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "h6",
                            weight: "bold",
                            children: connectedAppsPageTexts.noAppsConnected()
                        }),
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph1",
                            skin: "secondary",
                            children: connectedAppsPageTexts.appsDescription()
                        }),
                        /*#__PURE__*/ _jsx(Button, {
                            bi: {
                                name: 'explore_apps'
                            },
                            size: "medium",
                            onClick: onExploreApps,
                            className: styles.exploreAppsButton,
                            testId: ConnectedAppsPageTID.ExploreAppsButton(),
                            children: connectedAppsPageTexts.exploreApps()
                        })
                    ]
                })
            })
        })
    });
}
